import FrameSpec from "./FrameSpec";
import ArtworkEditor from "./ArtworkEditor";
import axios from "axios";
import { encode, decode } from "base-64";
import { omitBy, isEmpty } from "lodash";
import { sortLineItemProperties } from "./sortLineItemProperties";

import type {
  UploadedArtworkId,
  UploadedArtworkToken,
  RotationCount,
  Edits,
  ArtworkImage,
  ArtworkImageTypes,
  Artwork,
  OffsetInBundle,
  MouldingPlate,
  MatCaption,
  StoryPocket,
  MiniStoryPocket,
  KraftPaper,
  MinimalistFrameSpec,
  MinimalistFrameSpecs,
  DimensionsInPixels
} from "./types/interfaces/MinimalistFrameSpec";
import type {
  Number,
  HangingGuideSku,
  BundleMinimalistFrameSpec,
  BundleComponent,
  MinimalistBundleSpec,
  BundleSpecToken,
  MinimalistUploadTrayImage
} from "./types/interfaces/MinimalistBundleSpec";
import type {
  JoineryHost
} from "./types/interfaces/FrameSpec";
import type {
  FrameKey,
  ArtworkKey,
  ArtworkId,
  ArtworkEditorParams,
  ArtworkCore,
  BundleSpecNumber,
  ProductDetails
} from "./types/interfaces/BundleSpec";
import type { FullMatOpening, FullMats } from "./types/interfaces/Mats";
import type { FullMouldings } from "./types/interfaces/Mouldings";

type FrameSpecs = Array<FrameSpec>;
type Editor = any;
type Editors = Editor[];

export const imageTypes: ArtworkImageTypes[] = [
  "croppedAndNormalized",
  "customerSpecified",
  "normalized",
  "original",
  "originalUnedited",
  "normalizedUnedited"
];

export const rotateDimensions = (
  originalDimensions: DimensionsInPixels,
  rotate: RotationCount
): DimensionsInPixels => {
  return rotate % 2 === 0
    ? originalDimensions
    : {
      width: originalDimensions.height,
      height: originalDimensions.width
    };
};

const getFrameKey = (id: ArtworkId | FrameKey): FrameKey => {
  return (typeof id === "number")? id : id.frameKey;
};

class BundleSpec {
  number?: Number;
  authorizationToken?: BundleSpecToken;
  hangingGuideSku: HangingGuideSku;
  frameSpecs: FrameSpecs;
  editors: Editors;
  joineryHost: JoineryHost;
  _usedFullMats: FullMats;
  multiBundleSpec?: BundleSpec;
  photoTray?: MinimalistUploadTrayImage[];
  _allFullMats?: FullMats;
  _allFullMouldings?: FullMouldings;

  constructor (
    frameSpecs: FrameSpecs,
    editors: Editors,
    joineryHost: JoineryHost,
    bundleSpecData: MinimalistBundleSpec,
    usedFullMats: FullMats,
    multiBundleSpec?: BundleSpec,
    allFullMats?: FullMats,
    allFullMouldings?: FullMouldings,
  ) {
    this.number = bundleSpecData?.number || undefined;
    this.hangingGuideSku = bundleSpecData?.hangingGuideSku || "";
    this.frameSpecs = frameSpecs;
    this.editors = editors;
    this.joineryHost = joineryHost;
    this.multiBundleSpec = multiBundleSpec || undefined;
    this.photoTray = bundleSpecData?.photoTray || undefined;
    this._usedFullMats = usedFullMats;
    this._allFullMats = allFullMats || undefined;
    this._allFullMouldings = allFullMouldings || undefined;
  }

  static async artworkEditor ({
    frameSpec,
    useNormalized = false,
    edits = undefined,
    artworkKey = undefined
  }: ArtworkEditorParams) {
    return await ArtworkEditor.create({
      glazeClient: null,
      img: null,
      frameSpec,
      mafArtworks: [],
      selectedMafOpening: null,
      checkForQuality: null,
      fixedArtWidth: null,
      fixedArtHeight: null,
      useNormalized,
      edits,
      artworkKey
    });
  }
  static async editArtwork (params: ArtworkEditorParams) {
    const editor = await this.artworkEditor(params);
    await editor.applyEdits();

    return editor;
  }

  static frameSpecsFromBundle (
    bundleSpecData: MinimalistBundleSpec
  ): MinimalistFrameSpecs {
    return bundleSpecData.components.map((frame) => {
      return {
        ...frame.frameSpec,
        offsetInBundle: {
          leftInInches: frame.fromLeft,
          topInInches: frame.fromTop
        }
      };
    });
  }
  static async artworkCroppedAndNormalized (
    frameSpec: FrameSpec | MinimalistFrameSpec,
    artwork: Artwork,
    artworkKey: ArtworkKey
  ) {
    if (
      typeof artwork.normalizedUnedited !== "undefined" &&
      typeof artwork.originalUnedited !== "undefined" &&
      typeof artwork.edits !== "undefined" && (
        !artwork.croppedAndNormalized || (
          artwork.croppedAndNormalized.url &&
          artwork.croppedAndNormalized.url.includes("blob") 
        )
      )
    ) {
      const cropZone = artwork.edits.cropZone;
      const normalizedToOriginal =
        artwork.normalizedUnedited.dimensionsInPixels.width
        / artwork.originalUnedited.dimensionsInPixels.width;
      const normalizedCropZone = {
        width: Math.floor(cropZone.width * normalizedToOriginal),
        height: Math.floor(cropZone.height * normalizedToOriginal),
        top: Math.floor(cropZone.top * normalizedToOriginal),
        left: Math.floor(cropZone.left * normalizedToOriginal)
      };

      const editor = await this.editArtwork({
        frameSpec,
        useNormalized: true,
        edits: {
          ...artwork.edits,
          cropZone: normalizedCropZone
        },
        artworkKey
      });

      return {
        ...artwork,
        croppedAndNormalized: {
          url: editor.currentImageObjectURL,
          dimensionsInPixels: {
            width: normalizedCropZone.width,
            height: normalizedCropZone.height
          }
        }
      };
    } else {
      return artwork;
    } 
  }
  static async generateCroppedAndNormalized (
    frameSpecs: MinimalistFrameSpecs
  ): Promise<MinimalistFrameSpecs> {
    return await Promise.all(frameSpecs.map(async (frameSpec) => {
      const artworks = await Promise.all(frameSpec.artworks.map(async (artwork, artworkKey) => {
        return this.artworkCroppedAndNormalized(frameSpec, artwork, artworkKey);
      }));

      return {
        ...frameSpec,
        artworks
      };
    }));
  }
  static async initFrameSpecs (
    frameSpecs: MinimalistFrameSpecs,
    joineryHost: JoineryHost,
    usedFullMats?: FullMats,
    allFullMats?: FullMats,
    allFullMouldings?: FullMouldings,
  ) {
    return await Promise.all(frameSpecs.map(async (frameSpec) => {
      delete frameSpec.templateIdentifier;

      const fs = await FrameSpec.build({
        frameSpecData: frameSpec,
        number: frameSpec.id as number,
        joineryHost,
        usedFullMats,
        allFullMats,
        allFullMouldings,
        inBundle: true
      });

      await fs.calculateFinalFrameSize();

      // without await in case it won't resolve
      // disabling since we don't have shopify product id on FrameSpecs
      // fs.loadShopifyProduct();

      return fs;
    }));
  }
  static async initEditor (
    frameSpec: FrameSpec,
    artwork: Artwork,
    artworkKey?: ArtworkKey
  ) {
    if (
      artwork.normalizedUnedited && artwork.normalizedUnedited.url &&
      artwork.originalUnedited && artwork.originalUnedited.url &&
      artwork.edits
    ) {
      const rotate = artwork.edits ? artwork.edits.rotationCount || 0 : 0;
      const dimensions = rotateDimensions(artwork.normalizedUnedited.dimensionsInPixels, rotate);
      const cropZone = {
        width: dimensions.width,
        height: dimensions.height,
        top: 0,
        left: 0
      };

      const editor = await this.artworkEditor({
        frameSpec,
        useNormalized: true,
        edits: {
          cropZone,
          rotationCount: rotate,
          grayscale: artwork.edits.grayscale || false
        },
        artworkKey: artworkKey || 0
      });
      return editor;
    } else {
      return null;
    }
  }
  static async initEditors (frameSpecs: FrameSpec[]) {
    return await Promise.all(frameSpecs.map((frameSpec) => {
      return Promise.all(frameSpec.artworks.map(async (artwork, artworkKey) => {
        return await this.initEditor(frameSpec, artwork, artworkKey);
      }));
    }));
  }
  
  static prependHost (url: string, host: JoineryHost) {
    return url.startsWith("http") ? url : `${host}${url}`;
  }
  static prependHostToArtworkUrl (artwork: ArtworkImage, host: JoineryHost): ArtworkImage {
    return {
      dimensionsInPixels: artwork.dimensionsInPixels,
      url: this.prependHost(artwork.url, host)
    };
  }
  static prependHostToFrameSpecUrls<FS extends MinimalistFrameSpec | BundleMinimalistFrameSpec> (
    frameSpec: FS,
    host: JoineryHost
  ): FS {
    return {
      ...frameSpec,
      artworks: [
        {
          ...frameSpec.artworks[0],
          ...imageTypes.reduce((acc, imageType) => {
            const artwork = frameSpec.artworks[0][imageType] as ArtworkImage | undefined;
            return artwork
              ? {
                ...acc,
                [imageType]: artwork.url && artwork.dimensionsInPixels
                  ? this.prependHostToArtworkUrl(artwork, host)
                  : undefined
              } : acc;
          }, {})
        }
      ],
      moulding: {
        ...frameSpec.moulding,
        url: this.prependHost(frameSpec.moulding.url, host)
      }
    };
  }
  static removeArtworkImages (artwork: Artwork): Artwork {
    const artworkCopy = structuredClone(artwork);
    for (const imageType of imageTypes) {
      delete artworkCopy[imageType];
    }
    delete artworkCopy.artworkId;
    delete artworkCopy.token;
    return {
      ...artworkCopy,
      conveyance: 'uploaded'
    };
  }
  static openingsForFrame (
    frameSpec: BundleMinimalistFrameSpec,
    fullMats: FullMats
  ): FullMatOpening[] {
    if (frameSpec.matTemplate) {
      return frameSpec.matTemplate.openings;
    }

    const matName = frameSpec.mats[0].name || '';
    const mat = fullMats.find((mat) => mat.name === matName);

    return (mat && mat.openings) || [];
  }

  static parseOpeningsToArtworks (
    bundleSpec: MinimalistBundleSpec,
    fullMats: FullMats
  ): MinimalistBundleSpec {
    return {
      ...bundleSpec,
      components: bundleSpec.components.map((component) => {
        if (!component.frameSpec.mats.length) {
          return component;
        }
        const openings = BundleSpec.openingsForFrame(component.frameSpec, fullMats);

        if (openings.length < 2) {
          return component;
        }

        return {
          ...component,
          frameSpec: {
            ...component.frameSpec,
            artworks: openings.map<Artwork>((opening) => ({
              ...BundleSpec.removeArtworkImages(component.frameSpec.artworks[0]),
              exterior: {
                widthInInches: opening.artworkWidthInInches,
                heightInInches: opening.artworkHeightInInches,
                depthInInches: 0,
                artworkOpeningWidthInInches: opening.widthInInches,
                artworkOpeningHeightInInches: opening.heightInInches
              },
              offset: {
                topInInches: opening.topInInches,
                leftInInches: opening.leftInInches
              }
            }))
          }
        };
      })
    }
  }

  static async loadProductDetails (
    productId: string,
    joineryHost: JoineryHost
  ): Promise<ProductDetails> {
    const res = await axios.get(`${joineryHost}/api/v1/shopify_product_details/${productId}.json`);
    return res.data;
  }
  static async loadBundleSpec (
    bundleSpecNumber: string,
    joineryHost: JoineryHost,
    authorizationToken?: BundleSpecToken
  ): Promise<MinimalistBundleSpec> {
    const authHeaders = authorizationToken ? { "Framebridge-Resource-Authorization-Token": authorizationToken } : {};
    const res = await axios.get(`${joineryHost}/api/v1/bundle_specifications/${bundleSpecNumber}`, { headers: authHeaders });
    return {
      ...res.data,
      authorizationToken: authorizationToken,
      components: res.data.components.map((component: BundleComponent) => ({
        ...component,
        frameSpec: this.prependHostToFrameSpecUrls<BundleMinimalistFrameSpec>(component.frameSpec, joineryHost)
      }))
    };
  }

  static getAllUsedMatNames (bundleSpecData: MinimalistBundleSpec): string[] {
    const usedMats = bundleSpecData.components.map((component) => {
      return component.frameSpec.mats.map((mat) => mat.name);
    });

    return [...new Set(usedMats.flat())];
  }

  static async build ({
    bundleSpecData,
    joineryHost,
    usedFullMats,
    allFullMats,
    allFullMouldings,
  }: {
    bundleSpecData: MinimalistBundleSpec
    joineryHost: JoineryHost
    usedFullMats?: FullMats
    allFullMats?: FullMats,
    allFullMouldings?: FullMouldings,
  }): Promise<BundleSpec> {

    const ensuredUsedFullMats = usedFullMats
      ? usedFullMats
      : await FrameSpec.getMatsByNames(
          BundleSpec.getAllUsedMatNames(bundleSpecData),
          joineryHost
        );

    const minimalistFrameSpecs = this.frameSpecsFromBundle(bundleSpecData);
    const displayableFrameSpecs = await this.generateCroppedAndNormalized(
      minimalistFrameSpecs
    );
    const frameSpecs = await this.initFrameSpecs(
      displayableFrameSpecs,
      joineryHost,
      usedFullMats,
      allFullMats,
      allFullMouldings,
    );
    const editors = await this.initEditors(frameSpecs);

    const multiBundleSpec = bundleSpecData?.multiBundleSpec
      ? await BundleSpec.build({
        bundleSpecData: bundleSpecData.multiBundleSpec,
        joineryHost: joineryHost,
        usedFullMats,
        allFullMats,
      })
      : undefined;

    return new BundleSpec(
      frameSpecs,
      editors,
      joineryHost,
      bundleSpecData,
      ensuredUsedFullMats,
      multiBundleSpec,
      allFullMats,
      allFullMouldings,
    );
  }

  deleteBundleNumber () {
    delete this.number;
  }
  
  toMinimalistBundleSpec (): MinimalistBundleSpec {
    return {
      number: this.number,
      hangingGuideSku: this.hangingGuideSku,
      components: this.frameSpecs.map(frameSpec => ({
        fromLeft: frameSpec.offsetInBundle.leftInInches,
        fromTop: frameSpec.offsetInBundle.topInInches,
        frameSpec
      }))
    };
  }
  async clone () {
    const bundleSpecData = this.toMinimalistBundleSpec();
    return await BundleSpec.build({
      bundleSpecData,
      joineryHost: this.joineryHost,
      usedFullMats: this._usedFullMats,
      allFullMats: this._allFullMats,
      allFullMouldings: this._allFullMouldings,
    });
  }
  async duplicate () {
    const clonedBundleSpec = await this.clone();
    clonedBundleSpec.deleteBundleNumber();
    return clonedBundleSpec;
  }
  
  indicateLocalChanges (artworkId: ArtworkId) {
    this.deleteBundleNumber();
    this.deleteFrameNumber(artworkId);
    this.deleteArtworkImage(artworkId, 'customerSpecified');

    const artworkIdFrame = { ...artworkId, artworkKey: 0 };
    this.multiBundleSpec?.deleteFrameNumber(artworkIdFrame);
    this.multiBundleSpec?.deleteArtworkImage(artworkIdFrame, 'customerSpecified');
  }

  frame (id: ArtworkId | FrameKey) {
    return this.frameSpecs[getFrameKey(id)];
  }
  artwork (artworkId: ArtworkId) {
    return this.frameSpecs[artworkId.frameKey].artworks[artworkId.artworkKey];
  }
  artworkEdits (artworkId: ArtworkId): Edits {
    const artwork = this.artwork(artworkId);
    return {
      ...(artwork.edits || {}),
      ...(this.editor(artworkId).edits || {})
    };
  }
  editor (artworkId: ArtworkId) {
    return this.editors[artworkId.frameKey][artworkId.artworkKey];
  }
  allArtworkIdsNested () {
    return [...Array(this.frameSpecs.length).keys()].reduce<Array<ArtworkId[]>>((acc, frameKey) => {
      return [
        ...acc,
        [...Array(this.frameSpecs[frameKey].artworks.length).keys()].map<ArtworkId>((artworkKey) => ({
          frameKey,
          artworkKey
        }))
      ];
    }, []);
  }
  allArtworkIds () {
    return this.allArtworkIdsNested().flat();
  }
  nextArtworkId (artworkId: ArtworkId): ArtworkId | null {
    const allArtworkIds = this.allArtworkIds();
    const idx = allArtworkIds.findIndex((id) => (
      id.frameKey === artworkId.frameKey && id.artworkKey === artworkId.artworkKey
    ));
    if (idx === -1 || (idx !== -1 && idx === allArtworkIds.length - 1)) {
      return null;
    }

    return allArtworkIds[idx + 1];
  }
  correspondingFrameKey (offsetInBundle: OffsetInBundle) {
    const tolerance = 1;
    const frameKey = this.frameSpecs.findIndex((frameSpec) => {
      return Math.abs(frameSpec.offsetInBundle.leftInInches - offsetInBundle.leftInInches) <= tolerance
        && Math.abs(frameSpec.offsetInBundle.topInInches - offsetInBundle.topInInches) <= tolerance;
    })
    return frameKey;
  }
  areAllFramesUploaded (
    config: {
      withFrameSpec?: boolean,
      withArtwork?: boolean,
      skipMultis?: boolean
    } = {}
  ) {
    const {
      withFrameSpec,
      withArtwork,
      skipMultis
    } = {
      withFrameSpec: true,
      withArtwork: false,
      skipMultis: false,
      ...config
    };

    const notUploadedFrameIdx = this.frameSpecs
      .filter((frameSpec) => skipMultis ? frameSpec.artworks.length <= 1 : true)
      .findIndex(
        (frameSpec) => (
          (withFrameSpec && frameSpec.artworks.length === 1 ? !frameSpec.number : false) ||
          (withArtwork ? frameSpec.artworks.find((artwork) => !artwork.customerSpecified) : false)
        )
      );
    return notUploadedFrameIdx === -1;
  }

  editorCurrentArtwork (artworkId: ArtworkId) {
    const originalDimensions = this.artwork(artworkId)
      .originalUnedited?.dimensionsInPixels as DimensionsInPixels;
    return {
      url: this.editor(artworkId).currentImageObjectURL,
      dimensionsInPixels: rotateDimensions(
        originalDimensions,
        this.editor(artworkId).edits.rotationCount
      )
    };
  }
  async editorToggleGrayscale (artworkId: ArtworkId) {
    this.editor(artworkId).toggleGrayscale();
    await this.editor(artworkId).applyEdits();

    return this.editorCurrentArtwork(artworkId);
  }
  async editorRotate (artworkId: ArtworkId, direction: "left" | "right"): Promise<ArtworkImage> {
    if (direction === "left") {
      this.editor(artworkId).rotateCounterClockwise();
    } else {
      this.editor(artworkId).rotateClockwise();
    }
    await this.editor(artworkId).applyEdits();

    return this.editorCurrentArtwork(artworkId);
  }

  setEditor (artworkId: ArtworkId, editor: Editor) {
    this.editors[artworkId.frameKey][artworkId.artworkKey] = editor;
  }
  async resetEditor (artworkId: ArtworkId) { 
    const editor = await BundleSpec.artworkEditor({
      frameSpec: this.frameSpecs[artworkId.frameKey],
      useNormalized: true,
      artworkKey: artworkId.artworkKey
    });
    this.editors[artworkId.frameKey][artworkId.artworkKey] = editor;
  }
  async reinitEditor (artworkId: ArtworkId) {
    const editor = await BundleSpec.initEditor(this.frame(artworkId), this.artwork(artworkId), artworkId.artworkKey);
    this.editors[artworkId.frameKey][artworkId.artworkKey] = editor;
  }

  setEdits (artworkId: ArtworkId, edits: Edits) {
    this.frameSpecs[artworkId.frameKey].artworks[artworkId.artworkKey].edits = edits;
  }
  deleteFrameNumber (id: ArtworkId | FrameKey) {
    delete this.frameSpecs[getFrameKey(id)].number;
  }

  setArtworks (id: ArtworkId | FrameKey, artworks: Array<Artwork>) {
    const frameKey = getFrameKey(id);
    this.frameSpecs[frameKey].artworks = artworks;
  }
  setArtworkId (artworkId: ArtworkId, uploadedArtworkId: UploadedArtworkId) {
    this.frameSpecs[artworkId.frameKey].artworks[artworkId.artworkKey].artworkId = uploadedArtworkId;
  }
  setArtworkToken (artworkId: ArtworkId, uploadedArtworkToken: UploadedArtworkToken) {
    this.frameSpecs[artworkId.frameKey].artworks[artworkId.artworkKey].token = uploadedArtworkToken;
  }
  setArtworkImage (artworkId: ArtworkId, type: ArtworkImageTypes, image: ArtworkImage) {
    this.frameSpecs[artworkId.frameKey].artworks[artworkId.artworkKey][type] = image;
  }
  deleteArtworkImage (
    artworkId: ArtworkId,
    imageType: ArtworkImageTypes
  ) {
    delete this.frameSpecs[artworkId.frameKey].artworks[artworkId.artworkKey][imageType];
  }
  resetArtwork (artworkId: ArtworkId) {
    delete this.frameSpecs[artworkId.frameKey].id;
    for (const imageType of imageTypes) {
      this.deleteArtworkImage(artworkId, imageType);
    }
    delete this.frameSpecs[artworkId.frameKey].artworks[artworkId.artworkKey].artworkId;
    delete this.frameSpecs[artworkId.frameKey].artworks[artworkId.artworkKey].token;
    delete this.frameSpecs[artworkId.frameKey].artworks[artworkId.artworkKey].edits;

    this.editors[artworkId.frameKey][artworkId.artworkKey] = null;
  }
  setArtworkProps (artworkId: ArtworkId, artwork: ArtworkCore) {
    this.setArtworkId(artworkId, artwork.artworkId);
    this.setArtworkToken(artworkId, artwork.token);
    this.setArtworkImage(artworkId, 'croppedAndNormalized', artwork.croppedAndNormalized);
    this.setArtworkImage(artworkId, 'customerSpecified', artwork.customerSpecified);
    this.setArtworkImage(artworkId, 'normalized', artwork.normalized);
    this.setArtworkImage(artworkId, 'original', artwork.original);
    this.setArtworkImage(artworkId, 'originalUnedited', artwork.originalUnedited);
    this.setArtworkImage(artworkId, 'normalizedUnedited', artwork.normalizedUnedited);
  }
  setMouldingPlate (frameKey: FrameKey, mouldingPlate?: MouldingPlate) {
    if (!mouldingPlate || (
      mouldingPlate
      && mouldingPlate.line1 === ''
      && mouldingPlate.line2 === ''
    )) {
      this.frameSpecs[frameKey].removeMouldingPlate();
    } else {
      this.frameSpecs[frameKey].setMouldingPlate(mouldingPlate);
    }
  }
  setMatCaption (frameKey: FrameKey, matCaption?: MatCaption) {
    if (!matCaption) {
      this.frameSpecs[frameKey].removeMatCaption();
    } else {
      this.frameSpecs[frameKey].setMatCaption(matCaption);
    }
  }
  setStoryPocket (frameKey: FrameKey, storyPocket?: StoryPocket) {
    if (!storyPocket || (storyPocket && storyPocket.body === undefined)) {
      this.frameSpecs[frameKey].removeStoryPocket();
    } else {
      this.frameSpecs[frameKey].setStoryPocket(storyPocket);
    }
  }
  setMiniStoryPocket (frameKey: FrameKey, miniStoryPocket?: MiniStoryPocket) {
    if (!miniStoryPocket || (miniStoryPocket && miniStoryPocket.body === undefined)) {
      this.frameSpecs[frameKey].removeMiniStoryPocket();
    } else {
      this.frameSpecs[frameKey].setMiniStoryPocket(miniStoryPocket);
    }
  }
  setKraftPaper (frameKey: FrameKey, kraftPaper?: KraftPaper) {
    if (!kraftPaper || (kraftPaper && !kraftPaper.sku)) {
      this.frameSpecs[frameKey].removeKraftPaper();
    } else {
      this.frameSpecs[frameKey].setKraftPaper(kraftPaper);
    }
  }
  isMultiArtwork (artworkId: ArtworkId) {
    return artworkId.artworkKey > 0 || this.frameSpecs[artworkId.frameKey].artworks.length > 1;
  }

  async saveToServer (uploadTray?: MinimalistUploadTrayImage[]): Promise<{
    bundleSpecNumber: BundleSpecNumber
    token: BundleSpecToken
  }> {
    let response;
    const data = {
      include_token: true,
      bundle_specification: {
        hanging_guide_sku: this.hangingGuideSku,
        components_attributes: this.frameSpecs.map((frameSpec) => ({
          fs_number: frameSpec.number,
          from_top: frameSpec.offsetInBundle.topInInches,
          from_left: frameSpec.offsetInBundle.leftInInches
        })),
        photo_tray: uploadTray || null
      }
    };
    console.log("saving this BundleSpec -> ", data);

    response = await axios.post(`${this.joineryHost}/api/v1/bundle_specifications`, data);
    console.log("got back this BundleSpec -> ", response.data);
    this.number = response.data.number;
    this.authorizationToken = response.data.authorizationToken;
    return {
      bundleSpecNumber: response.data.number,
      token: response.data.authorizationToken
    };
  }

  async shareViaEmail ({
    url,
    email
  }: {
    url: string,
    email: string
  }) {
    if (!this.number || !this.authorizationToken) {
      throw new Error('BundleSpec number and authorization token are required.');
    }

    return await axios({
      method: 'post',
      url: `${this.joineryHost}/api/v1/share_bundle_spec/${this.number}`,
      headers: {
        'Framebridge-Resource-Authorization-Token': this.authorizationToken
      },
      data: {
        bundle_spec_url: url,
        email
      }
    });
  }

  async addToCart ({
    shopifyVariantId,
    shopifyCustomerId,
    cartHost,
    lineProperties = {},
    lineItemNumber,
    addOnLineItems
  }: {
    shopifyVariantId: string
    shopifyCustomerId?: string
    cartHost?: string,
    lineProperties?: object,
    lineItemNumber?: string,
    addOnLineItems?: object[]
  }) {

    await Promise.allSettled(
      this.frameSpecs.map(frameSpec => frameSpec.autoCrop({ saveToServer: true }))
    );

    lineProperties = omitBy(lineProperties, isEmpty);
    const localShopifyCustomerId = shopifyCustomerId || (
      // @ts-ignore
      window && window.SDG && window.SDG.Data && decode(window.SDG.Data.cid)
    )
    const cartUrl = cartHost || 'https://underpass.staging.framebridge.io';

    if (lineItemNumber) {
      const removeResult = await axios.post(
        `${cartUrl}/api/cart/remove?cid=${encode(localShopifyCustomerId)}`,
        { lineId: lineItemNumber },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (removeResult.status !== 200) {
        throw new Error();
      }
    }

    // Sort: hidden properties first (A-Z), then visible (A-Z) for consistent Shopify admin display
    const sortedProperties = sortLineItemProperties({
      '_bundle_spec_number': this.number,
      '_bundle_spec_authorization_token': this.authorizationToken,
      ...lineProperties
    });

    let result;
    result = await axios.post(
      `${cartUrl}/api/cart/add?cid=${encode(localShopifyCustomerId)}`,
      {
        items: [
          {
            id: shopifyVariantId,
            properties: sortedProperties
          },
          ...(addOnLineItems ? addOnLineItems : [])
        ]
      },
      { headers: { 'Content-Type': 'application/json' } }
    );
    if (result.status !== 200) {
      throw new Error();
    }

    return result;
  }
}

export default BundleSpec;
